import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PublicNav, Footer } from "@/components/layout/PublicLayout";
import { useContent } from "@/context/ContentContext";
import api from "@/lib/api";
import {
  ArrowRight, Zap, ShieldCheck, LineChart, Headphones, CheckCircle2,
  UserPlus, Wallet, MousePointerClick, Rocket, Play, X, Instagram,
  Youtube, Facebook, Send, Twitter, MoreHorizontal, Music2,
} from "lucide-react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/3a86e399-0f5e-4929-89b9-b6f221d174ba/images/17ee1eda890f1ef2a9dce97148189e6fe2cff3a17f2c9e719659a9034ce5f3f2.jpeg";

const YT_ID = /^[A-Za-z0-9_-]{11}$/;

export default function Landing() {
  const { t } = useContent();
  const [videos, setVideos] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/homepage/videos").then((r) => setVideos(Array.isArray(r.data) ? r.data : [])).catch(() => setVideos([]));
  }, []);

  const platforms = [
    { icon: Instagram, name: "Instagram", sub: t("home.svc.instagram", "Followers, Likes, Vues, Commentaires"), cls: "from-fuchsia-600 to-orange-500", slug: "instagram" },
    { icon: Music2, name: "TikTok", sub: t("home.svc.tiktok", "Followers, Likes, Vues, Partages"), cls: "from-slate-700 to-slate-900", slug: "tiktok" },
    { icon: Youtube, name: "YouTube", sub: t("home.svc.youtube", "Vues, Abonnés, Likes, Commentaires"), cls: "from-red-600 to-red-800", slug: "youtube" },
    { icon: Facebook, name: "Facebook", sub: t("home.svc.facebook", "Followers, Likes, Partages, Vues"), cls: "from-blue-600 to-blue-800", slug: "facebook" },
    { icon: Send, name: "Telegram", sub: t("home.svc.telegram", "Membres, Vues, Réactions"), cls: "from-sky-500 to-cyan-600", slug: "telegram" },
    { icon: Twitter, name: "X (Twitter)", sub: t("home.svc.x", "Followers, Likes, Reposts, Vues"), cls: "from-slate-800 to-black", slug: "x" },
    { icon: MoreHorizontal, name: t("home.svc.other", "Autres services"), sub: t("home.svc.other_sub", "Discord, Spotify, LinkedIn, Twitch..."), cls: "from-indigo-600 to-violet-700", slug: "" },
  ];

  const advantages = [
    [Zap, t("home.adv.1.t", "Livraison automatisée"), t("home.adv.1.d", "Commandes traitées 24/7")],
    [ShieldCheck, t("home.adv.2.t", "Paiements sécurisés"), t("home.adv.2.d", "Vos fonds sont protégés")],
    [LineChart, t("home.adv.3.t", "Suivi en temps réel"), t("home.adv.3.d", "Statut détaillé de chaque commande")],
    [Headphones, t("home.adv.4.t", "Support réactif"), t("home.adv.4.d", "Une équipe à votre écoute")],
  ];

  const steps = [
    [UserPlus, t("home.step.1.t", "Créez un compte"), t("home.step.1.d", "Inscription rapide et gratuite")],
    [Wallet, t("home.step.2.t", "Ajoutez des fonds"), t("home.step.2.d", "Carte bancaire, Mobile Money ou USDT (TRC20)")],
    [MousePointerClick, t("home.step.3.t", "Choisissez un service"), t("home.step.3.d", "Parcourez notre catalogue")],
    [Rocket, t("home.step.4.t", "Passez commande"), t("home.step.4.d", "Recevez vos résultats et suivez en temps réel")],
  ];

  return (
    <div className="bg-[#020B18] text-white">
      <PublicNav />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_50%_at_75%_30%,rgba(37,99,235,0.18),transparent)]" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-5 py-16 lg:grid-cols-2 lg:py-24">
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-blue-500/40 bg-blue-500/10 px-3 py-1 text-xs font-medium text-blue-300">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-400" /> {t("home.hero.badge", "Votre croissance, notre priorité")}
            </div>
            <h1 className="mt-5 font-display text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
              {t("home.hero.title1", "Boostez votre présence")}<br />
              {t("home.hero.title2", "sur les ")}<span className="text-blue-500">{t("home.hero.title_hl", "réseaux sociaux")}</span>
            </h1>
            <p className="mt-5 max-w-lg text-base text-slate-300 sm:text-lg">
              {t("home.hero.subtitle", "Des services de promotion fiables, rapides et sécurisés pour développer votre audience et atteindre vos objectifs.")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-cta-primary" className="group inline-flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition-all hover:bg-blue-500 hover:shadow-blue-500/40">
                {t("home.hero.cta1", "Créer un compte gratuitement")} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <a href="#videos" data-testid="hero-cta-secondary" className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-white/10">
                <Play className="h-4 w-4" /> {t("home.hero.cta2", "Voir la vidéo de présentation")}
              </a>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-400">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-blue-400" /> {t("home.hero.trust1", "Inscription en moins d'une minute")}</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-blue-400" /> {t("home.hero.trust2", "Paiement sécurisé")}</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-blue-400" /> {t("home.hero.trust3", "Accès immédiat au catalogue")}</span>
            </div>
          </div>
          <div className="relative animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="relative mx-auto max-w-md overflow-hidden rounded-3xl border border-blue-500/20 bg-gradient-to-b from-blue-500/5 to-transparent shadow-2xl">
              <img src={HERO_IMG} alt={t("home.hero.img_alt", "The World Promotions — croissance sur les réseaux sociaux")} loading="eager" className="w-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* ADVANTAGES BAR */}
      <section className="border-b border-white/5 bg-[#06111F]">
        <div className="mx-auto grid max-w-7xl gap-6 px-5 py-8 sm:grid-cols-2 lg:grid-cols-4">
          {advantages.map(([Ico, ttl, d], i) => (
            <div key={i} className="flex items-start gap-3" data-testid={`advantage-${i}`}>
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-600/15 text-blue-400"><Ico className="h-5 w-5" /></div>
              <div><div className="text-sm font-semibold">{ttl}</div><div className="text-xs text-slate-400">{d}</div></div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="mx-auto max-w-7xl px-5 py-16">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-blue-400">{t("home.svc.eyebrow", "Nos services")}</div>
            <h2 className="mt-2 font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("home.svc.title", "Toutes les plateformes, un seul endroit")}</h2>
            <p className="mt-2 text-slate-400">{t("home.svc.subtitle", "Instagram, TikTok, YouTube, Facebook, Telegram, X et bien plus encore.")}</p>
          </div>
          <Link to="/services" className="inline-flex items-center gap-2 text-sm font-medium text-blue-400 hover:underline">{t("home.svc.link", "Voir tous les services")} <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-7">
          {platforms.map((p, i) => (
            <Link key={i} to={p.slug ? `/services?platform=${p.slug}` : "/services"} data-testid={`platform-card-${p.slug || "other"}`} className={`group relative overflow-hidden rounded-xl border border-white/10 bg-gradient-to-br ${p.cls} p-4 transition-transform hover:-translate-y-1`}>
              <p.icon className="h-6 w-6 text-white" />
              <div className="mt-6 text-sm font-semibold text-white">{p.name}</div>
              <div className="mt-1 text-[11px] leading-tight text-white/70">{p.sub}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* VIDEOS */}
      <section id="videos" className="mx-auto max-w-7xl px-5 py-16">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-blue-400">{t("home.vid.eyebrow", "Vidéos officielles TWP")}</div>
            <h2 className="mt-2 font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("home.vid.title", "Découvrez TWP en vidéo")}</h2>
            <p className="mt-2 text-slate-400">{t("home.vid.subtitle", "Regardez nos vidéos de présentation et nos tutoriels directement sur notre site.")}</p>
          </div>
          <Link to="/services" className="inline-flex items-center gap-2 text-sm font-medium text-blue-400 hover:underline">{t("home.vid.link", "Voir toutes les vidéos")} <ArrowRight className="h-4 w-4" /></Link>
        </div>
        {videos.length === 0 ? (
          <div data-testid="videos-empty" className="mt-8 rounded-2xl border border-dashed border-white/10 bg-[#06111F] p-12 text-center">
            <Play className="mx-auto h-8 w-8 text-slate-600" />
            <p className="mt-3 text-sm text-slate-400">{t("home.vid.empty", "Vidéos bientôt disponibles.")}</p>
          </div>
        ) : (
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {videos.filter((v) => YT_ID.test(v.youtube_video_id || "")).map((v) => (
              <button key={v.id || v.youtube_video_id} data-testid={`video-card-${v.youtube_video_id}`} onClick={() => setActive(v)} className="group overflow-hidden rounded-xl border border-white/10 bg-[#06111F] text-left transition-transform hover:-translate-y-1">
                <div className="relative aspect-video overflow-hidden">
                  <img src={`https://i.ytimg.com/vi/${v.youtube_video_id}/hqdefault.jpg`} alt={v.title} loading="lazy" className="h-full w-full object-cover" />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 transition-colors group-hover:bg-black/20">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-600/90 text-white"><Play className="h-5 w-5" /></span>
                  </div>
                  {v.duration && <span className="absolute bottom-2 right-2 rounded bg-black/70 px-1.5 py-0.5 text-[10px] text-white">{v.duration}</span>}
                </div>
                <div className="p-4">
                  <div className="text-sm font-semibold">{v.title}</div>
                  {v.description && <div className="mt-1 text-xs text-slate-400">{v.description}</div>}
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-7xl px-5 py-8">
        <div className="rounded-3xl border border-white/10 bg-[#06111F] p-8 lg:p-12">
          <div className="text-xs font-semibold uppercase tracking-wider text-blue-400">{t("home.how.eyebrow", "Comment ça marche ?")}</div>
          <h2 className="mt-2 font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("home.how.title", "Quatre étapes simples")}</h2>
          <div className="mt-10 grid gap-8 md:grid-cols-4">
            {steps.map(([Ico, ttl, d], i) => (
              <div key={i} className="relative" data-testid={`step-${i + 1}`}>
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-sm font-bold text-white">{`0${i + 1}`}</span>
                  <Ico className="h-5 w-5 text-blue-400" />
                </div>
                <h3 className="mt-4 font-display font-semibold">{ttl}</h3>
                <p className="mt-1 text-sm text-slate-400">{d}</p>
                {i < 3 && <ArrowRight className="absolute -right-4 top-1 hidden h-5 w-5 text-slate-600 md:block" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BLUE CTA */}
      <section className="mx-auto max-w-7xl px-5 py-12">
        <div className="overflow-hidden rounded-3xl bg-gradient-to-r from-blue-700 to-blue-500 p-10 text-center lg:p-14">
          <h2 className="font-display text-3xl font-bold sm:text-4xl">{t("home.cta.title", "Prêt à développer votre audience ?")}</h2>
          <p className="mx-auto mt-3 max-w-lg text-sm text-white/80">{t("home.cta.subtitle", "Rejoignez notre plateforme et commencez dès maintenant.")}</p>
          <Link to="/register" data-testid="final-cta" className="mt-8 inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3 text-sm font-semibold text-blue-700 transition-transform hover:scale-105">
            {t("home.cta.button", "Créer un compte gratuitement")} <ArrowRight className="h-4 w-4" />
          </Link>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-6 text-xs text-white/90">
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> {t("home.cta.b1", "Rapide")}</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> {t("home.cta.b2", "Sécurisé")}</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> {t("home.cta.b3", "Sans engagement")}</span>
          </div>
        </div>
      </section>

      <Footer />

      {/* VIDEO MODAL — YouTube embed, stays on TWP */}
      {active && YT_ID.test(active.youtube_video_id || "") && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4" onClick={() => setActive(null)} data-testid="video-modal">
          <div className="w-full max-w-3xl overflow-hidden rounded-2xl border border-white/10 bg-[#06111F]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3">
              <div className="text-sm font-semibold text-white">{active.title}</div>
              <button onClick={() => setActive(null)} data-testid="video-modal-close" className="rounded-lg p-1 text-slate-400 hover:bg-white/10 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <div className="aspect-video w-full bg-black">
              <iframe className="h-full w-full" src={`https://www.youtube.com/embed/${active.youtube_video_id}?autoplay=1&rel=0`} title={active.title} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
            {active.description && <div className="px-4 py-3 text-xs text-slate-400">{active.description}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

export function SectionHead({ eyebrow, title, subtitle }) {
  return (
    <div className="max-w-2xl">
      {eyebrow && <div className="text-xs font-semibold uppercase tracking-wider text-blue-400">{eyebrow}</div>}
      <h2 className="mt-2 font-display text-3xl font-bold tracking-tight sm:text-4xl">{title}</h2>
      {subtitle && <p className="mt-3 text-slate-400">{subtitle}</p>}
    </div>
  );
}

export const faqItems = (t) => [
  { q: t("faq.q1", "Qu'est-ce que The World Promotions ?"), a: t("faq.a1", "Une marketplace de promotion sur les réseaux sociaux qui regroupe plusieurs fournisseurs derrière un catalogue unifié, avec livraison automatisée et suivi en temps réel.") },
  { q: t("faq.q2", "Comment fonctionne le portefeuille ?"), a: t("faq.a2", "Chaque client dispose d'un portefeuille interne en USD à registre. Chaque mouvement génère une transaction traçable ; aucun solde n'est modifié silencieusement.") },
  { q: t("faq.q3", "Quelles méthodes de paiement sont acceptées ?"), a: t("faq.a3", "Carte bancaire (Visa/Mastercard), Mobile Money via PawaPay, et USDT sur le réseau TRC20 uniquement.") },
  { q: t("faq.q4", "Que signifient Économie, Standard et Premium ?"), a: t("faq.a4", "Ce sont des niveaux de qualité configurés par l'administrateur, indiquant la stabilité attendue, la chute potentielle et la disponibilité de recharge.") },
  { q: t("faq.q5", "Puis-je suivre mes commandes ?"), a: t("faq.a5", "Oui, chaque commande dispose d'un statut en direct et d'un historique d'événements détaillé, du démarrage jusqu'à la livraison.") },
  { q: t("faq.q6", "Existe-t-il une API pour revendeurs ?"), a: t("faq.a6", "Oui, vous pouvez générer une clé API depuis votre tableau de bord pour créer des commandes et consulter votre solde par programmation.") },
];
